import React, { useState, useEffect } from "react"
import Modal from "./modal"
import * as endpoints from "../../api/endpoints"
import * as endpoint from "../../booking/constants/Endpoints"
import { emailValidatingRegex, hosts } from "../itinerary/itinerary-module"
import { isValidPhoneNumber } from "react-phone-number-input"
import reactStringReplace from "react-string-replace"
import * as SelectionFactoryHelper from "../helpers/SelectionFactoryHelper"
import LoginLink from "../../booking/components/common/loginLink"
import Spinner from "../../booking/components/spinner"
import SimpleErrorModal from "./simple-error-modal"
import { isMobile } from "react-device-detect"
import axios from "axios"
import { trackTravelPlanner } from "../../helpers/tracking"
import { useStore } from "../store/commonStore"

export default function FlightSignupModal({
  profile,
  flightInfo,
  departureFlight,
  arrivalFlight,
  textKeys,
  travelType,
  travelInformation,
  marketingConsent,
  trackingEventLabel,
  language,
}) {
  const { flightSignUpModalOpen, setFlightSignUpModalOpen } = useStore()

  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [validEmail, setValidEmail] = useState(true)
  const [validPhoneNumber, setValidPhoneNumber] = useState(true)
  const [validSmsCode, setValidSmsCode] = useState(true)
  const [validAcceptedTerms, setValidAcceptedTerms] = useState(true)
  const [isSuccess, setIsSuccess] = useState(false)
  const [verifyPhone, setVerifyPhone] = useState(false)
  const [isLoggedInWithApp, setIsLoggedInWithApp] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isLoggedInWithPhone, setIsLoggedInWithPhone] = useState(false)
  const [ajaxSpinnerActive, setAjaxSpinnerActive] = useState(false)
  const [ajaxSpinnerTransparent, setAjaxSpinnerTransparent] = useState(false)
  const [appPermission, setAppPermission] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [phoneInputError, setPhoneInputError] = useState("")
  const [errorData, setErrorData] = useState({})
  const [smsCode, setSmsCode] = useState("")

  useEffect(() => {
    if (profile) {
      setIsLoggedIn(true)
    }
    setProfileState()
  }, [profile])

  function setProfileState() {
    let email = ""
    let phoneNumber = ""
    let acceptedTerms = false

    email = profile.EmailAddress || ""
    phoneNumber = (profile.ContactDetails && profile.ContactDetails.MobilePhone) || ""
    acceptedTerms = profile.ContactDetails && profile.ContactDetails.NewsletterSubscription

    const isLoggedInWithPhone = profile.ContactDetails && profile.ContactDetails.MobilePhone && profile.ContactDetails.MobilePhoneVerified === true
    const isLoggedInWithApp = profile.ContactDetails && profile.ContactDetails.MobileOsToken // MobileAppGuid is null in CRM
    setEmail(email)
    setPhoneNumber(phoneNumber)
    setAcceptedTerms(acceptedTerms)
    setIsLoggedInWithPhone(isLoggedInWithPhone)
    setIsLoggedInWithApp(isLoggedInWithApp)
  }

  function handleShowErrorModal(error) {
    setShowErrorModal(true)
    setErrorData({
      h3: error,
      richText: textKeys?.errorMessage,
    })
  }

  function validateEmail(email) {
    return emailValidatingRegex.test(String(email).toLowerCase())
  }

  function validatePhoneNumber(phoneNumber) {
    const allowedCountries = textKeys?.smsServiceSupportedCountryCodes || ["+45"]
    const strippedNum = phoneNumber.replaceAll(" ", "")
    const countryCode = strippedNum.substring(0, 3)
    const phoneValid = isValidPhoneNumber(phoneNumber)
    if (phoneValid) {
      if (allowedCountries.includes(countryCode)) {
        setPhoneInputError(textKeys?.invalidSmsLabel || "Invalid phone number")
        return phoneValid
      } else {
        setPhoneInputError(textKeys?.smsServiceNotSupportedText || "Unfortunately, the SMS service is not valid for your area code")
        return false
      }
    } else {
      setPhoneInputError(textKeys?.invalidSmsLabel || "Invalid phone number")
      return phoneValid
    }
  }

  function handleInput(event) {
    const { name, value } = event.target
    if (name === "email") setEmail(value)
    if (name === "phoneNumber") setPhoneNumber(value)
    if (name === "smsCode") setSmsCode(value)
    setValidEmail(true)
    setValidPhoneNumber(true)
  }

  function handleCheck(event) {
    const { name, checked } = event.target
    if (name === "acceptedTerms") setAcceptedTerms(checked)
    if (name === "appPermission") setAppPermission(checked)
    setValidAcceptedTerms(true)
  }

  function createMarkup(html) {
    return { __html: html }
  }

  function validateInputs() {
    let validationFailed = false

    // terms validation
    if (!acceptedTerms) {
      setValidAcceptedTerms(false)
      validationFailed = true
    } else {
      setValidAcceptedTerms(true)
    }

    // email validation
    if (!email || !validateEmail(email)) {
      setValidEmail(false)
      validationFailed = true
    } else {
      setValidEmail(true)
    }

    // phone number validation
    if (phoneNumber) {
      if (!validatePhoneNumber(phoneNumber)) {
        setValidPhoneNumber(false)
        validationFailed = true
      } else {
        setValidPhoneNumber(true)
      }
    }
    return !validationFailed
  }

  async function handleSubmitClick(e) {
    e.preventDefault()
    let response

    if (!validateInputs()) {
      return
    }
    trackTravelPlanner("track-event.generic", "follow flight - submit", trackingEventLabel, flightInfo.FlightId)
    setAjaxSpinnerActive(true)
    response = await subscribe(flightInfo?.FlightId, "Travel Planner")

    setAjaxSpinnerActive(false)
    if (response.errorMsg || !response.success) {
      handleShowErrorModal(textKeys.emailErrorHeadline)
    } else if (phoneNumber && !isLoggedInWithPhone && !verifyPhone) {
      setVerifyPhone(true)
    } else {
      setIsSuccess(true)
    }
  }

  function handleCodeSubmit(e) {
    e.preventDefault()
    if (!smsCode || smsCode.length !== 6) {
      setValidSmsCode(false)
    } else {
      trackTravelPlanner("track-event.travelplanner_follow_flight", "follow flight - validate", trackingEventLabel, flightInfo.FlightId)

      setAjaxSpinnerActive(true)
      const url = endpoint.VERIFY_PHONE_3(hosts)
      const data = {
        EmailAddress: email,
        PhoneNumber: phoneNumber,
        ActivationCode: smsCode,
      }
      axios
        .post(url, data)
        .then((res) => {
          if (res && res.data && res.data.item1) {
            setIsSuccess(true)
          } else {
            handleShowErrorModal(textKeys.smsErrorMsg)
          }
        })
        .catch((error) => {
          console.log(error)
          handleShowErrorModal(textKeys.smsErrorMsg)
        })
        .finally(() => {
          setAjaxSpinnerActive(false)
        })
    }
  }

  async function subscribe(flightId, registrationSource) {
    let success = false
    let errorMsg = ""
    try {
      const res = await endpoints.subscribeToFlight(
        flightId,
        email,
        phoneNumber,
        appPermission,
        travelType,
        isMobile,
        language,
        acceptedTerms,
        registrationSource,
        marketingConsent?.permissionVersion,
      )
      success = res && res.data
    } catch (error) {
      console.log(error)
      errorMsg = error.message
    }
    return { success: success, errorMsg: errorMsg }
  }

  function getSuccessMessage() {
    if (isLoggedIn) {
      if (appPermission) return textKeys?.thankYouAppConfirmationText || `Du vil modtage alle flyinformationer i din app`
      if (phoneNumber || smsCode)
        return textKeys?.thankYouSmsConfirmationText?.replace("{phone}", phoneNumber) || `Du er tilmeldt flyinformationer på ${phoneNumber}`
      if (email) return textKeys?.thankYouEmailConfirmationText?.replace("{email}", email) || `Du er tilmeldt flyinformationer på ${email}`
    }
    if (smsCode) return textKeys?.thankYouSmsConfirmationText?.replace("{phone}", phoneNumber) || `Du er tilmeldt flyinformationer på ${phoneNumber}`
    else return textKeys?.thankYouEmailConfirmationText?.replace("{email}", email) || `Du er tilmeldt flyinformationer på ${email}`
  }

  const confirmButtonColor = SelectionFactoryHelper.getFromSelectionFactory(textKeys?.confirmButtonColor)
  const flightInformationBoxColor = SelectionFactoryHelper.getFromSelectionFactory(textKeys?.flightInformationBoxColor)

  const loginLink =
    textKeys?.alreadyUser &&
    reactStringReplace(textKeys?.alreadyUser, /\{([^}]+)\}/g, (match, i) => (
      <LoginLink
        key={i}
        linkClassName={"follow-flight-login-link"}
        loginurl={endpoint.ACCOUNT_LOGIN(hosts, flightInfo?.language)}
        linkText={match.replace(/\{|\}/gi, "")}
      />
    ))

  const flights = (
    <div className={`infobox ${flightInformationBoxColor ? flightInformationBoxColor : ""}`}>
      {`${flightInfo?.Flight} - ${isMobile ? flightInfo?.CarrierNameMobile : flightInfo?.CarrierName}`}
      <br />
      {`${flightInfo?.DestinationName.split("-")[0]}`}
    </div>
  )

  if (showErrorModal) {
    return <SimpleErrorModal errorData={errorData} modalOpen={showErrorModal} />
  }

  if (isSuccess) {
    return (
      <Modal
        isOpen={isSuccess}
        noScrollToTop="true"
        modifier="modal--wide modal--fixed"
        overlayModifier="ReactModal__Overlay--fixed"
        bodyModifier="ReactModal__Body--open ReactModal--no-overflow"
        closeHandler={() => setFlightSignUpModalOpen(false)}
      >
        <div className="departure-info-modal">
          <h1>{textKeys?.thankYouTitle}</h1>
          <div className="grid">
            <div className="grid-item col--12 col-sm--12">
              <div className="departure-info__essentials__standard-info">
                <div className="rich-text">{getSuccessMessage()}</div>
              </div>
              {flights}
            </div>
          </div>
          <div className="departure-info__essentials__dualbuttons">
            <div className="textbuttoncontainer">
              <div className="cta-button">
                <a className={`button button--small button--general casc-blue-dark`} onClick={() => setFlightSignUpModalOpen(false)}>
                  <span className="text">{textKeys?.thankYouCloseText}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  if (verifyPhone) {
    return (
      <Modal
        isOpen={verifyPhone}
        noScrollToTop="true"
        modifier="modal--narrow modal--fixed"
        overlayModifier="ReactModal__Overlay--fixed"
        bodyModifier="ReactModal__Body--open ReactModal--no-overflow"
        closeHandler={() => setFlightSignUpModalOpen(false)}
      >
        <Spinner isActive={ajaxSpinnerActive} isTransparent={ajaxSpinnerTransparent} />
        <div className="departure-info-modal">
          <h1>{textKeys?.titleVerifyPhone}</h1>
          {flights}
          <div className="departure-info__essentials__standard-info">
            <div className="rich-text" dangerouslySetInnerHTML={createMarkup(textKeys?.subTitleVerifyPhone)}></div>
            <form className="grid-item col--12 col-lg--6 form" onSubmit={(e) => handleCodeSubmit(e)}>
              <div className="form__field form__field--text form__field--text--no-label is-bound">
                <input
                  className="form__item signup-email"
                  name="smsCode"
                  type="number"
                  value={smsCode}
                  placeholder={textKeys?.smsCodePlaceholder || "SMS kode"}
                  onChange={(e) => handleInput(e)}
                />
                <span className={`${validSmsCode ? "d-none" : ""} form__notice form__notice--box is-invalid`}>{textKeys.smsCodeInputError}</span>
              </div>
            </form>
          </div>
          <div className="departure-info__essentials__dualbuttons">
            <div className="textbuttoncontainer">
              <div className="cta-button">
                <a className={`button button--small button--general ${confirmButtonColor}`} onClick={(e) => handleCodeSubmit(e)}>
                  <span className="text">{textKeys?.confirmButtonText}</span>
                </a>
              </div>
              <div className="back-button">
                <a className="cancellink pointer" onClick={() => setFlightSignUpModalOpen(false)}>
                  <span className="text">{textKeys.cancelButtonText || "Annuller"}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  const title = isLoggedInWithPhone ? textKeys?.titleLoggedInWithPhone : isLoggedIn ? textKeys?.titleLoggedInNoPhone : textKeys?.titleNotLoggedIn
  const subTitle = isLoggedInWithPhone
    ? textKeys?.subTitleLoggedInWithPhone
    : isLoggedIn
      ? textKeys?.subTitleLoggedInNoPhone
      : textKeys?.subTitleNotLoggedIn

  return (
    <Modal
      isOpen={flightSignUpModalOpen}
      noScrollToTop="true"
      modifier="modal--narrow modal--fixed"
      overlayModifier="ReactModal__Overlay--fixed"
      bodyModifier="ReactModal__Body--open ReactModal--no-overflow"
      closeHandler={() => setFlightSignUpModalOpen(false)}
    >
      <Spinner isActive={ajaxSpinnerActive} isTransparent={ajaxSpinnerTransparent} />
      <div className="departure-info-modal">
        <h1>{title}</h1>
        {flights}
        <div className="departure-info__essentials__standard-info">
          <div className="rich-text" dangerouslySetInnerHTML={createMarkup(subTitle)}></div>
          <form className="grid-item col--12 col-lg--6 form" onSubmit={(e) => handleSubmitClick(e)}>
            <div className={`form__field form__field--text form__field--text--no-label is-bound is-required ${validEmail ? "" : "is-invalid"}`}>
              <input
                className="form__item signup-email"
                name="email"
                type="email"
                placeholder={textKeys?.emailPlaceholder || "email adress"}
                value={email}
                onChange={(e) => handleInput(e)}
              />
              <span className={`${validEmail ? "d-none" : ""} form__notice form__notice--box is-invalid`}>{textKeys?.invalidEmailLabel}</span>
            </div>
            <div className="form__field form__field--text form__field--text--no-label is-bound">
              <input
                className="form__item signup-email"
                name="phoneNumber"
                type="tel"
                value={phoneNumber}
                placeholder={textKeys?.smsPlaceholder || "Phone number"}
                onChange={(e) => handleInput(e)}
              />
              <span className={`${validPhoneNumber ? "d-none" : ""} form__notice form__notice--box is-invalid`}>{phoneInputError}</span>
            </div>
            {isLoggedInWithApp && (
              <>
                <div className="form__field form__field--checkbox is-bound">
                  <label className="form__label">
                    <input
                      className="form__item signup-checkbox "
                      name="appPermission"
                      id="appPermission"
                      type="checkbox"
                      onChange={(e) => handleCheck(e)}
                    />
                    <span className="form__label__text large-text">
                      <p>{textKeys?.appLabel}</p>
                    </span>
                  </label>
                </div>
                <div className="rich-text">
                  <p>{textKeys?.appText}</p>
                </div>
              </>
            )}
            {!isLoggedIn ? (
              <div className="login-text">
                <p>{loginLink}</p>
              </div>
            ) : (
              ""
            )}
          </form>
        </div>
        <div className="departure-info__essentials__acceptcheck">
          <div className="form__section form__section--short">
            <div className="form__field form__field--checkbox is-bound is-required">
              <label className="form__label">
                <input
                  className="form__item signup-checkbox "
                  name="acceptedTerms"
                  id="acceptedTerms"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => handleCheck(e)}
                />
                <span className="form__label__text">
                  <h5>{marketingConsent?.texts?.headline}</h5>
                  <span dangerouslySetInnerHTML={createMarkup(marketingConsent?.texts?.bodyTextRich)}></span>
                </span>
              </label>
            </div>
            <span className={`${validAcceptedTerms ? "d-none" : ""} form__notice form__notice--box is-invalid`}>{textKeys?.invalidAcceptTerms}</span>
          </div>
        </div>
        <div className="departure-info__essentials__dualbuttons">
          <div className="textbuttoncontainer">
            <div className="cta-button">
              <a className={`button button--small button--general ${confirmButtonColor}`} onClick={(e) => handleSubmitClick(e)}>
                <span className="text">{textKeys?.confirmButtonText}</span>
              </a>
            </div>
            <div className="back-button">
              <a className="cancellink pointer" onClick={() => setFlightSignUpModalOpen(false)}>
                <span className="text">{textKeys.cancelButtonText || "Annuller"}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
